import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Activity,
  Check,
  ChevronDown,
  Database,
  Globe,
  KeyRound,
  Loader2,
  Network,
  Rocket,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import { IntegrationLogo } from "@/components/integrations/logo";
import { INTEGRATIONS } from "@/data/integrations";
import { cn } from "@/lib/utils";

/**
 * Validate that a slug exists in the runtime REGISTRY. Used to keep
 * showcase data honest — every illustrative tool / agent we render
 * resolves to a real on-disk logo.
 */
function fromRegistry(slug: string): string {
  if (!INTEGRATIONS.some((i) => i.slug === slug)) {
    throw new Error(`agent-deploy-showcase: slug "${slug}" not in REGISTRY`);
  }
  return slug;
}

/**
 * AgentDeployShowcase v3 — fixed-dimension, interactive MemKernel
 * dashboard. Same layout on every viewport; surrounding page never
 * reflows. Three tabs (Configure / Pipeline / Live) auto-advance every
 * few seconds; users can click any tab to pin it, which pauses
 * auto-advance for ~6s before resuming.
 */

const PHASES = [
  { label: "Wiring tool registry",   icon: Workflow },
  { label: "Mounting memory stores", icon: Database },
  { label: "Indexing vector store",  icon: Sparkles },
  { label: "Linking OAuth credentials", icon: KeyRound },
  { label: "Applying permission rules", icon: ShieldCheck },
  { label: "Provisioning MCP endpoint", icon: Network },
] as const;

// Each entry's `slug` resolves to a real packages/integrations service
// folder; `name` is an illustrative tool reference / agent label.
const PRESET_TOOLS = [
  { slug: fromRegistry("googlecalendar"),   name: "calendar.create_event" },
  { slug: fromRegistry("googlesheets"),     name: "sheets.append_row"     },
  { slug: fromRegistry("microsoft_teams"),  name: "teams.post_message"    },
  { slug: fromRegistry("notion"),           name: "notion.append_page"    },
] as const;

const AGENTS = [
  { slug: fromRegistry("anthropic_administrator"), name: "market-scout",    detail: "Claude Sonnet · p50 47 ms", metric: "1,284 / hr" },
  { slug: fromRegistry("openai"),                  name: "support-triage",  detail: "GPT-4o · p50 31 ms",         metric: "812 / hr"   },
  { slug: fromRegistry("gemini"),                  name: "weekly-briefing", detail: "Gemini · cron 0 9 * * MON",  metric: "1 / week"   },
  { slug: fromRegistry("replicate"),               name: "code-reviewer",   detail: "Replicate · p50 88 ms",      metric: "245 / hr"   },
] as const;

type Tab = 1 | 2 | 3;

const TABS: { id: Tab; label: string; short: string; icon: typeof Workflow }[] = [
  { id: 1, label: "Configure",  short: "Config", icon: Workflow },
  { id: 2, label: "Pipeline",   short: "Wire",   icon: Activity },
  { id: 3, label: "Live fleet", short: "Live",   icon: Globe   },
];

// Auto-advance pacing.
const TAB_DURATION_MS = 3600;
const PAUSE_AFTER_CLICK_MS = 6000;

export function AgentDeployShowcase() {
  const reduce = useReducedMotion();
  const [tab, setTab] = useState<Tab>(1);
  const pausedUntil = useRef(0);
  const visibleRef = useRef(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-advance tabs.
  useEffect(() => {
    if (reduce) return;
    const id = window.setInterval(() => {
      if (!visibleRef.current) return;
      if (Date.now() < pausedUntil.current) return;
      setTab((t) => ((t % 3) + 1) as Tab);
    }, TAB_DURATION_MS);
    return () => window.clearInterval(id);
  }, [reduce]);

  // Pause when scrolled out of view.
  useEffect(() => {
    if (!containerRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = !!entry?.isIntersecting;
      },
      { threshold: 0.15 },
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  function pickTab(next: Tab) {
    pausedUntil.current = Date.now() + PAUSE_AFTER_CLICK_MS;
    setTab(next);
  }

  return (
    <div
      ref={containerRef}
      className="mx-auto flex h-[620px] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/40 shadow-2xl backdrop-blur sm:h-[640px]"
    >
      {/* Browser chrome */}
      <div className="flex items-center gap-3 border-b border-border/40 bg-background/40 px-3 py-2.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
        </div>
        <div className="flex flex-1 items-center justify-center gap-1.5 truncate rounded-md bg-background/80 px-3 py-1 font-mono text-[11px] text-muted-foreground">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
          <span className="truncate">app.memkernel.com/projects/market-intel/deploy</span>
        </div>
        <span className="hidden shrink-0 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] text-muted-foreground sm:inline">
          auto-cycling
        </span>
      </div>

      {/* Tab strip */}
      <div className="flex items-stretch border-b border-border/40 bg-background/20">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => pickTab(t.id)}
            className={cn(
              "relative flex flex-1 items-center justify-center gap-1.5 px-2 py-2.5 text-[11px] font-medium transition-colors sm:flex-none sm:gap-2 sm:px-4 sm:py-3 sm:text-[12px]",
              tab === t.id
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold sm:h-4 sm:w-4",
                tab === t.id
                  ? "bg-foreground text-background"
                  : "bg-foreground/10 text-muted-foreground",
              )}
            >
              {t.id}
            </span>
            <t.icon className="hidden h-3.5 w-3.5 sm:inline" />
            <span className="sm:hidden">{t.short}</span>
            <span className="hidden sm:inline">{t.label}</span>
            {tab === t.id && (
              <motion.span
                layoutId="adp-active-tab"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-foreground"
                transition={{ type: "spring", duration: 0.4, bounce: 0.2 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Content panel fills the rest of the locked container height */}
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={reduce ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 overflow-y-auto p-4 sm:p-6"
          >
            {tab === 1 && <ConfigurePanel onDeploy={() => pickTab(2)} />}
            {tab === 2 && (
              <PipelinePanel
                active={tab === 2}
                onDone={() => pickTab(3)}
              />
            )}
            {tab === 3 && <LivePanel />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Tab 1: Configure ──────────────────────────────────────────────

function ConfigurePanel({ onDeploy }: { onDeploy: () => void }) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Deploy a new agent</h3>
          <p className="text-[11px] text-muted-foreground">
            Pick a project — tools, memory, vectors auto-attach
          </p>
        </div>
        <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground">
          draft
        </span>
      </div>

      <div className="space-y-3">
        <Field label="Project">
          <div className="flex items-center justify-between rounded-lg border border-foreground/15 bg-background/60 px-3 py-2 text-[13px] font-medium">
            <span className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-teal-400 text-[10px] font-bold text-white">
                M
              </span>
              market-intel
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </Field>

        <Field
          label="Tools"
          hint={<span className="text-emerald-400">4 auto-attached</span>}
        >
          <div className="space-y-1.5">
            {PRESET_TOOLS.map((t) => (
              <div
                key={t.slug}
                className="flex items-center gap-2 rounded-md border border-foreground/10 bg-background/50 px-2 py-1.5 text-[12px]"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-white p-0.5 ring-1 ring-black/5">
                  <IntegrationLogo slug={t.slug} name={t.name} size={14} />
                </span>
                <span className="flex-1 truncate font-mono">{t.name}</span>
                <Check className="h-3 w-3 shrink-0 text-emerald-400" />
              </div>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Memory">
            <Pill icon={<Database className="h-3 w-3" />}>knowledge-base</Pill>
          </Field>
          <Field label="Vectors">
            <Pill icon={<Sparkles className="h-3 w-3" />}>papers-index</Pill>
          </Field>
        </div>
      </div>

      <button
        type="button"
        onClick={onDeploy}
        className="mt-auto flex w-full items-center justify-center gap-2 rounded-lg bg-foreground py-2.5 text-sm font-semibold text-background shadow-lg transition-transform active:scale-[0.98]"
      >
        <Rocket className="h-3.5 w-3.5" />
        Deploy
      </button>
    </div>
  );
}

// ── Tab 2: Pipeline ───────────────────────────────────────────────

function PipelinePanel({
  active,
  onDone,
}: {
  active: boolean;
  onDone: () => void;
}) {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);
  const ticksPerPhase = 5;
  const total = PHASES.length * ticksPerPhase + 6;
  const phaseIdx = Math.min(Math.floor(tick / ticksPerPhase), PHASES.length);
  const isWiring = tick < PHASES.length * ticksPerPhase;
  const allDone = tick >= PHASES.length * ticksPerPhase;

  useEffect(() => {
    if (!active || reduce) {
      if (reduce) setTick(total);
      return;
    }
    setTick(0);
    const id = window.setInterval(() => {
      setTick((t) => {
        const next = t + 1;
        if (next === PHASES.length * ticksPerPhase + 3) onDone();
        return Math.min(next, total);
      });
    }, 90);
    return () => window.clearInterval(id);
  }, [active, reduce, onDone, total]);

  const progressPct = Math.min(
    (tick / (PHASES.length * ticksPerPhase)) * 100,
    100,
  );

  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">MemKernel auto-wires</h3>
          <p className="text-[11px] text-muted-foreground">
            Stack is already configured — no human in the loop
          </p>
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors",
            allDone
              ? "border border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
              : "border border-foreground/15 bg-foreground/5 text-muted-foreground",
          )}
        >
          <span className="relative flex h-1.5 w-1.5">
            {allDone && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
            )}
            <span
              className={cn(
                "relative inline-flex h-1.5 w-1.5 rounded-full",
                allDone ? "bg-emerald-400" : "bg-muted-foreground",
              )}
            />
          </span>
          {allDone ? "ready" : "wiring"}
        </span>
      </div>

      <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-foreground/5">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-teal-300"
          initial={false}
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.08, ease: "linear" }}
        />
      </div>

      <ul className="flex-1 space-y-1.5">
        {PHASES.map((p, i) => {
          const done = isWiring ? i < phaseIdx : allDone;
          const isActive = isWiring && i === phaseIdx;
          return (
            <li
              key={p.label}
              className={cn(
                "flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-[12px] transition-colors",
                isActive && "border-foreground/10 bg-foreground/5",
                !done && !isActive && "opacity-50",
              )}
            >
              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                  done
                    ? "border-emerald-400/60 bg-emerald-400/15 text-emerald-300"
                    : isActive
                      ? "border-foreground/30 bg-foreground/10 text-foreground"
                      : "border-foreground/15 text-muted-foreground",
                )}
              >
                {done ? (
                  <Check className="h-3 w-3" />
                ) : isActive ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <p.icon className="h-3 w-3" />
                )}
              </span>
              <span className={cn("truncate", done && "text-muted-foreground")}>
                {p.label}
              </span>
            </li>
          );
        })}
      </ul>

      <motion.div
        initial={false}
        animate={{ opacity: allDone ? 1 : 0, y: allDone ? 0 : 6 }}
        transition={{ duration: 0.3 }}
        className="mt-3 flex items-center justify-between rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2"
      >
        <div className="flex items-center gap-2 text-[12px] font-medium text-emerald-300">
          <Rocket className="h-3.5 w-3.5" />
          Deployed
        </div>
        <span className="font-mono text-[11px] font-medium text-emerald-300/80">
          live on every region
        </span>
      </motion.div>
    </div>
  );
}

// ── Tab 3: Live fleet ─────────────────────────────────────────────

function LivePanel() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Live fleet</h3>
          <p className="text-[11px] text-muted-foreground">
            All agents healthy
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          {AGENTS.length} live
        </span>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-2">
        {AGENTS.map((a, i) => (
          <motion.div
            key={a.name}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className="flex items-center justify-between gap-3 rounded-lg border border-foreground/10 bg-background/40 px-3 py-2"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white p-1 ring-1 ring-black/5">
                <IntegrationLogo slug={a.slug} name={a.name} size={20} />
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/80" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400 ring-1 ring-background" />
                </span>
              </span>
              <div className="min-w-0">
                <p className="truncate font-mono text-[12px] font-medium">
                  {a.name}
                </p>
                <p className="truncate text-[10px] text-muted-foreground">
                  {a.detail}
                </p>
              </div>
            </div>
            <span className="shrink-0 rounded-md bg-foreground/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold tabular-nums text-foreground/80">
              {a.metric}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {hint && <span className="text-[10px]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Pill({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex w-full items-center gap-1.5 rounded-md border border-foreground/10 bg-background/50 px-2 py-1.5 font-mono text-[12px]">
      <span className="text-muted-foreground">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}
